import { SelectionModel } from '@angular/cdk/collections';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { toBoolean } from '@ngx-simple/core/coercion';
import { SimCellDefDirective, SimColumnDefDirective, SimFooterCellDefDirective, SimHeaderCellDefDirective } from './cell';
import { SimTableComponent } from './table.component';

/**
 * 复选框列，它仅显示标题单元格、页脚单元格和行单元格的文本内容。假设该表使用本机表实现('<table>')。
 *
 * 默认name：checkbox$
 * 标题文本可以被“headerText”输入覆盖。
 * 页脚文本可以被“footerText”输入覆盖。
 * 使用“justify”输入将文本的对齐更改为开始或结束。
 */
@Component({
  selector: 'sim-checkbox-column',
  template: `
    <ng-container simColumnDef>
      <th sim-header-cell *simHeaderCellDef class="sim-table-column-checkbox">
        <label class="sim-table-checkbox-label" *ngIf="multiple" (click)="_selectionchange($event, 'all')">
          <sim-pseudo-checkbox
            [state]="
              _selectionModel.hasValue() && _isAllSelected()
                ? 'checked'
                : _selectionModel.hasValue() && !_isAllSelected()
                ? 'indeterminate'
                : 'unchecked'
            "
          ></sim-pseudo-checkbox>
          {{ headerText }}
        </label>
      </th>
      <td sim-cell *simCellDef="let row" class="sim-table-column-checkbox">
        <label class="sim-table-checkbox-label" (click)="_selectionchange($event, 'cell', row)">
          <sim-pseudo-checkbox [state]="_selectionModel.isSelected(row) ? 'checked' : 'unchecked'"></sim-pseudo-checkbox>
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        </label>
      </td>
      <td sim-footer-cell *simFooterCellDef class="sim-table-column-checkbox">
        <label class="sim-table-checkbox-label" *ngIf="multiple" (click)="_selectionchange($event, 'invert'); _invertState = !_invertState">
          <sim-pseudo-checkbox [state]="_invertState ? 'checked' : 'unchecked'"></sim-pseudo-checkbox>
          {{ footerText }}
        </label>
      </td>
    </ng-container>
  `,
  encapsulation: ViewEncapsulation.None,
  // 有意不将更改检测设置为OnPush。将提供该组件的模板到要插入到其视图中的表。
  // 这在以后运行变更检测时是有问题的这个模板中的绑定将在表的视图被计算之后进行计算在表的视图中的模板将没有更新的值(实际上将导致一个ExpressionChangedAfterItHasBeenCheckedError)。
  changeDetection: ChangeDetectionStrategy.Default
})
export class SimCheckboxColumnComponent<T> implements OnDestroy, OnInit {
  /** 复选框当选多选单选 默认多选 只能初始化设置一次 后面设置将不生效 */
  @Input() multiple: boolean = true;

  /** 用于引用此列的列名 */
  @Input()
  get name(): string {
    return this._name;
  }
  set name(name: string) {
    this._name = name;

    // 使用Ivy，可以在静态查询结果可用之前初始化输入。在这种情况下，我们延迟同步直到“ngOnInit”触发。
    this._syncColumnDefName();
  }
  _name: string = 'checkbox$';

  /**
   * 应用于列标题的文本标签。
   */
  @Input() headerText: string = '全选';

  /**
   * 应用于列页脚的文本标签。
   */
  @Input() footerText: string = '反选';

  /** 单元格值的对齐 */
  @Input() justify: 'start' | 'end' = 'start';

  /** 当勾选复选框时发出的事件。 */
  @Output() readonly checkedChange: EventEmitter<T[]> = new EventEmitter<T[]>();

  /**
   * columnDef在'ngOnInit'期间通过静态查询提供。
   */
  @ViewChild(SimColumnDefDirective, { static: true }) columnDef: SimColumnDefDirective;

  /**
   * cell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimCellDefDirective, { static: true }) cell: SimCellDefDirective;

  /**
   * headerCell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimHeaderCellDefDirective, { static: true }) headerCell: SimHeaderCellDefDirective;
  /**
   * footerCell在'ngOnInit'期间通过静态查询提供给columnDef。
   */
  @ViewChild(SimFooterCellDefDirective, { static: true }) footerCell: SimFooterCellDefDirective;

  _selectionModel: SelectionModel<T>;

  _invertState: boolean;

  constructor(@Optional() private _table: SimTableComponent<T>) {}

  ngOnInit() {
    // 我们不需要关系动态修改值变化 只要关系第一次赋值即可
    const multiple = toBoolean(this.multiple);
    this._selectionModel = new SelectionModel(multiple, []);

    this._syncColumnDefName();

    if (this._table) {
      // 使用静态的'ViewChild'查询直接向表提供'cell`和'headerCell`、'footerCell'，因为在表检查其内容并初始化行时，'columnDef'将不会获取其内容。
      this.columnDef.cell = this.cell;
      this.columnDef.headerCell = this.headerCell;
      this.columnDef.footerCell = this.footerCell;
      this._table.addColumnDef(this.columnDef);
    } else {
      throw Error(`Text column could not find a parent table for registration.`);
    }
  }

  ngOnDestroy() {
    if (this._table) {
      this._table.removeColumnDef(this.columnDef);
    }
  }

  /** 所选元素的数量是否与总行数匹配 */
  _isAllSelected() {
    const numSelected = this._selectionModel.selected.length;
    const numRows = this._table.data.length;
    return numSelected === numRows;
  }

  setCheckboxSelected(selected: (row: T) => boolean) {
    this._table.data.forEach(row => selected(row) && this._selectionModel.select(row));
  }

  _selectionchange(event: MouseEvent, type: 'all' | 'cell' | 'invert', item?: T) {
    if (event) {
      event.stopPropagation();
    }
    switch (type) {
      case 'all': // 全选
        this._isAllSelected() ? this._selectionModel.clear() : this._table.data.forEach(row => this._selectionModel.select(row));
        break;
      case 'cell': // 单选
        this._selectionModel.toggle(item);
        break;
      case 'invert': // 反选
        this._table.data.forEach(row => this._selectionModel.toggle(row));
        break;
      default:
        break;
    }
    this.checkedChange.next(this._selectionModel.selected);
  }

  /** 使列定义名称与文本列名同步 */
  private _syncColumnDefName() {
    if (this.columnDef) {
      this.columnDef.name = this.name;
    }
  }
}
